import { Skeleton } from 'antd';
import {
    TeamOutlined,
    GroupOutlined,
    ShareAltOutlined,
    BlockOutlined,
    MessageOutlined,
    VideoCameraOutlined,
    CalendarOutlined,
    RobotOutlined,
    FieldTimeOutlined,
    GlobalOutlined,
    PartitionOutlined,
    SplitCellsOutlined,
    RetweetOutlined,
} from '@ant-design/icons';
import { useNodeInfo } from '../../hooks/use_node_info';
import { getCapabilityGroups } from '../../roadmapData';
import './MapPage.css';

/**
 * The "map": what this node runs, as separate blocks rather than a sequence.
 *
 * Rendered as the Map tab of the node settings page, never as a page of its
 * own — hence no heading here (the tab is the heading) and no page centring.
 * Superadmin-only by virtue of where it's mounted, and deliberately not gated
 * on any feature flag: it exists to report which flags are on.
 *
 * Presentation for each capability lives here rather than in roadmapData.js so
 * that module stays JSX-free — the what's-new ribbon and the /legacy_roadmap
 * archive import it too, and neither wants icons pulled along.
 *
 * Colour encodes category, not decoration. An entity type carries the accent it
 * already has everywhere else in the app, so a Contexts block reads the same
 * green as a context does on the browse grid. Everything else is a node service
 * and shares the brand sky — so the signature strip below reads as four
 * distinct entity marks against a run of node-blue.
 */
const NODE_ACCENT = 'var(--vellby-sky-500)';

const BLOCK_STYLE = {
    people: { icon: <TeamOutlined />, color: 'var(--entity-person)' },
    context: { icon: <GroupOutlined />, color: 'var(--entity-context)' },
    relationship: { icon: <ShareAltOutlined />, color: 'var(--entity-relationship)' },
    app: { icon: <BlockOutlined />, color: 'var(--entity-app)' },
    messaging: { icon: <MessageOutlined />, color: NODE_ACCENT },
    hangouts: { icon: <VideoCameraOutlined />, color: NODE_ACCENT },
    schedule: { icon: <CalendarOutlined />, color: NODE_ACCENT },
    byoagent: { icon: <RobotOutlined />, color: NODE_ACCENT },
    schedules: { icon: <FieldTimeOutlined />, color: NODE_ACCENT },
    facilitator: { icon: <PartitionOutlined />, color: NODE_ACCENT },
    sidebyside: { icon: <SplitCellsOutlined />, color: NODE_ACCENT },
    regrouper: { icon: <RetweetOutlined />, color: NODE_ACCENT },
    federation: { icon: <GlobalOutlined />, color: NODE_ACCENT },
};

function MapPage() {
    const nodeInfo = useNodeInfo();
    const groups = getCapabilityGroups(nodeInfo);
    const blocks = groups.flatMap(group => group.blocks);
    const running = blocks.filter(block => block.live).length;
    const off = blocks.length - running;

    // The feature flags read permissive until /info lands, which would show
    // every block as running and then switch most of them off. On a page that
    // exists to report exactly those flags, wait instead.
    if (!nodeInfo.ready) {
        return (
            <div className="map-page">
                <Skeleton active paragraph={{ rows: 8 }} />
            </div>
        );
    }

    return (
        <div className="map-page">
            <p className="map-lede">
                {nodeInfo.nodeId || 'This node'} runs {running} of {blocks.length} capabilities.{' '}
                {off > 0
                    ? 'The rest are switched off here, not missing — each one can be turned on for this node.'
                    : 'Everything available is switched on.'}
            </p>

            {/* Same information as the sentence above, so it is decorative to a
                screen reader even though it is load-bearing visually. */}
            <div className="map-signature" aria-hidden="true">
                {blocks.map(block => (
                    <span
                        key={block.id}
                        className={`map-mark${block.live ? '' : ' map-mark--off'}`}
                        style={block.live ? { background: BLOCK_STYLE[block.id]?.color } : undefined}
                    />
                ))}
            </div>

            {groups.map(group => (
                <section className="map-group" key={group.name}>
                    <h2 className="map-group-name">{group.name}</h2>
                    <div className="map-blocks">
                        {group.blocks.map(block => {
                            const presentation = BLOCK_STYLE[block.id] || {};
                            const accent = block.live ? presentation.color : undefined;
                            return (
                                <article
                                    key={block.id}
                                    className={`map-block${block.live ? '' : ' map-block--off'}`}
                                    style={accent ? { borderLeftColor: accent } : undefined}
                                >
                                    <div className="map-block-head">
                                        <span className="map-block-icon" style={accent ? { color: accent } : undefined}>
                                            {presentation.icon}
                                        </span>
                                        <h3 className="map-block-title">{block.title}</h3>
                                    </div>
                                    <p className="map-block-desc">{block.description}</p>
                                    <p className="map-block-state">{block.live ? 'Running' : 'Available'}</p>
                                </article>
                            );
                        })}
                    </div>
                </section>
            ))}
        </div>
    );
}

export default MapPage;
